import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { loadVgFieldBoard } from '../db/company';
import { weekEnding } from './billingTicketDocs';
import strikeLogo from '../assets/logo.png';
import voltaLogo from '../assets/voltagrid.png';

// Trailer TV board for the VoltaGrid jobs, fed by VoltaGrid Field reports.
//
// Served on /billing-tv (a standalone Main with no menus) so the trailer PC
// keeps its bookmark. It answers, from across the room, for the last eight
// days (split where a billing week, Monday to Sunday, ends):
//
//   1. Left: which foremen have turned in their daily report in VoltaGrid
//      Field, day by day.
//   2. Right: the same foremen and days as keyed into HeavyJob — the time
//      cards — and whether a day's hours match the report.
//   3. Under them, one chart per job: the hours charged to each cost code
//      day by day (stacked bars, the busiest codes named, the rest as
//      Other), with each code's hours to date as a share of its budget.
//
// Feed: readVgFieldBoard, refreshed every five minutes — a plain read like
// the other Fusion read functions, because the trailer TV can't sign in
// (the vgField* actions need a VGF device or Microsoft token). It returns
// the report headers for the window, the hours and quantities each report
// put on each code, the code list (activity, UoM, budgets, hours to date)
// and the HeavyJob time cards for the same jobs. A foreman's day on a job
// that's in HeavyJob is checked against it and counts HeavyJob's hours in
// the charts.
//
// No dollars anywhere — the client walks through that trailer.

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export const GRID_DAYS = 14;
export const BOARD_DAYS = 8;
export const CODE_DAYS = 7;
const REFRESH_MS = 5 * 60 * 1000;

// ── Pure helpers (exported for the tests) ──

const ymd = (m) => m.format('YYYY-MM-DD');
const dayOf = (d) => String(d || '').slice(0, 10);

/** n calendar days ending today, oldest first. */
export const gridDays = (today, n = GRID_DAYS) => {
    const out = [];
    for (let i = n - 1; i >= 0; i -= 1) out.push(moment.utc(today, 'YYYY-MM-DD').subtract(i, 'day').format('YYYY-MM-DD'));
    return out;
};

/** The board's window: the last n days ending today (Seth 10/13: "last eight days"). */
export const boardDays = (today, n = BOARD_DAYS) => gridDays(today, n);

/** The days split into billing weeks (Monday to Sunday, weekEnding), oldest first: [{ weekEnd, days }]. */
export const weeksOf = (days) => {
    const out = [];
    (days || []).forEach((d) => {
        const we = weekEnding(d);
        const w = out[out.length - 1];
        if (w && w.weekEnd === we) w.days.push(d); else out.push({ weekEnd: we, days: [d] });
    });
    return out;
};

export const isSunday = (d) => moment.utc(d, 'YYYY-MM-DD').day() === 0;

const STATUS_RANK = { returned: 0, draft: 1, submitted: 2, approved: 3 };

// Report and HeavyJob hours closer than this count as the same.
const HJ_TOLERANCE = 0.25;

/**
 * HeavyJob cards folded per foreman × day: `${emp}|${date}` -> { emp, name,
 * date, hours, people, approved, byJob: { job: { hours, people, approved } } }.
 * People is the foreman's distinct people across the day's cards.
 */
export const hjDays = (cards) => {
    const out = {};
    (cards || []).forEach((c) => {
        const k = `${c.foremanEmp}|${dayOf(c.date)}`;
        const e = out[k] || (out[k] = { emp: String(c.foremanEmp), name: c.foremanName || '', date: dayOf(c.date), hours: 0, people: 0, approved: true, byJob: {} });
        const h = Number(c.hours) || 0;
        e.hours += h;
        e.people = Math.max(e.people, Number(c.peopleDay) || Number(c.people) || 0);
        e.approved = e.approved && !!c.approved;
        e.byJob[String(c.job)] = { hours: h, people: Number(c.people) || 0, approved: !!c.approved };
    });
    return out;
};

/**
 * Report vs HeavyJob for one foreman-day: per job, the report's hours
 * against the card's. { hours, people, approved, byJob, ok, diffs: [{ job,
 * report, hj }] } — a job on either side with different hours is a diff.
 */
export const hjCheck = (jobHours, hj) => {
    const jobs = new Set([...Object.keys(jobHours || {}), ...Object.keys(hj.byJob)]);
    const diffs = [];
    [...jobs].sort().forEach((j) => {
        const report = Number((jobHours || {})[j]) || 0;
        const card = hj.byJob[j] ? hj.byJob[j].hours : 0;
        if (Math.abs(report - card) > HJ_TOLERANCE) diffs.push({ job: j, report, hj: card });
    });
    return { hours: hj.hours, people: hj.people, approved: hj.approved, byJob: hj.byJob, ok: !diffs.length, diffs };
};

/**
 * One row per foreman: { key, emp, name, jobs[], cells: { date: cell } }
 * where a cell folds that foreman's reports for the day (one per job):
 * { status (the least-finished), hours, people, ids[], byJob: { job: status },
 *   jobHours: { job: hours }, hj (hjCheck, or null when HeavyJob doesn't have the day) },
 * or
 * { status: 'hj', hours, people, byJob, hj, missing } for a day HeavyJob has
 * and no report (missing when a report was due, as below),
 * or
 * { status: 'missing' } for a Mon–Sat day after their first report with
 * nothing filed (today never counts as missing — the shift may still be on).
 */
export const foremanRows = (headers, days, today, hjCards = []) => {
    const by = new Map();
    (headers || []).forEach((h) => {
        const d = dayOf(h.date);
        if (!days.includes(d)) return;
        const emp = String(h.foremanEmp || h.foremanName || '?');
        const job = String(h.job);
        const key = emp;
        const row = by.get(key) || { key, emp, name: h.foremanName || emp, jobs: new Set(), cells: {}, first: d };
        if (d < row.first) row.first = d;
        row.jobs.add(job);
        const c = row.cells[d] || { status: 'approved', hours: 0, people: 0, ids: [], byJob: {} };
        const st = STATUS_RANK[h.status] != null ? h.status : 'draft';
        if (STATUS_RANK[st] < STATUS_RANK[c.status]) c.status = st;
        c.hours += Number(h.hours) || 0;
        c.people = Math.max(c.people, Number(h.people) || 0);
        c.ids.push(h.id);
        c.jobHours = c.jobHours || {};
        const jh = h.jobHours && Object.keys(h.jobHours).length ? h.jobHours : { [job]: Number(h.hours) || 0 };
        Object.entries(jh).forEach(([j, v]) => { c.jobHours[j] = (c.jobHours[j] || 0) + (Number(v) || 0); });
        if (c.byJob[job] == null || STATUS_RANK[st] < STATUS_RANK[c.byJob[job]]) c.byJob[job] = st;
        row.cells[d] = c;
        by.set(key, row);
    });
    const hj = hjDays(hjCards);
    const rows = [...by.values()].map((r) => {
        days.forEach((d) => {
            const card = hj[`${r.emp}|${d}`];
            const due = d > r.first && d < today && !isSunday(d);
            if (r.cells[d]) {
                r.cells[d].hj = card ? hjCheck(r.cells[d].jobHours, card) : null;
            } else if (card) {
                r.cells[d] = {
                    status: 'hj', hours: card.hours, people: card.people, missing: due,
                    byJob: Object.fromEntries(Object.keys(card.byJob).map((j) => [j, 'hj'])),
                    hj: { hours: card.hours, people: card.people, approved: card.approved, byJob: card.byJob, ok: true, diffs: [] },
                };
            } else if (due) {
                r.cells[d] = { status: 'missing' };
            }
        });
        return { ...r, jobs: [...r.jobs].sort() };
    });
    return rows.sort((a, b) => a.jobs[0].localeCompare(b.jobs[0]) || a.name.localeCompare(b.name));
};

/** Foremen with HeavyJob hours in the window and no report on the board, A–Z. */
export const hjOnlyForemen = (hjCards, rows, days) => {
    const onBoard = new Set((rows || []).map((r) => String(r.emp)));
    const names = new Map();
    (hjCards || []).forEach((c) => {
        if (!days.includes(dayOf(c.date)) || !(Number(c.hours) > 0) || onBoard.has(String(c.foremanEmp))) return;
        names.set(String(c.foremanEmp), c.foremanName || String(c.foremanEmp));
    });
    return [...names.values()].sort((a, b) => a.localeCompare(b));
};

/**
 * The rows both grids share, so a foreman sits on the same line left and
 * right: foremanRows' rows, then any foreman with HeavyJob hours in the
 * window and no report, as { key, emp, name, jobs[], cells: {}, hjOnly: true },
 * each row carrying hj: { date: hjDays entry } for the HeavyJob grid.
 * Order: by first job, then name — HeavyJob-only foremen by their card's job.
 */
export const boardRows = (rows, hjCards, days) => {
    const hj = hjDays(hjCards);
    const onBoard = new Set((rows || []).map((r) => String(r.emp)));
    const extra = new Map();
    (hjCards || []).forEach((c) => {
        const emp = String(c.foremanEmp);
        if (!days.includes(dayOf(c.date)) || !(Number(c.hours) > 0) || onBoard.has(emp)) return;
        const r = extra.get(emp) || { key: emp, emp, name: c.foremanName || emp, jobs: new Set(), cells: {}, hjOnly: true };
        r.jobs.add(String(c.job));
        extra.set(emp, r);
    });
    const all = [
        ...(rows || []),
        ...[...extra.values()].map((r) => ({ ...r, jobs: [...r.jobs].sort() })),
    ];
    return all
        .map((r) => {
            const cards = {};
            days.forEach((d) => { const e = hj[`${r.emp}|${d}`]; if (e) cards[d] = e; });
            return { ...r, hj: cards };
        })
        .sort((a, b) => (a.jobs[0] || '').localeCompare(b.jobs[0] || '') || a.name.localeCompare(b.name));
};

/**
 * Hours and people per day and per week across the rows: { days: { date:
 * { hours, people } }, weeks: { weekEnd: { hours, people } } }. mode 'report'
 * sums the reports (a day HeavyJob has and no report counts nothing), 'hj'
 * the HeavyJob cards. People is the sum of each foreman's people.
 */
export const totalsOf = (rows, weeks, mode) => {
    const days = {};
    const wk = {};
    (weeks || []).forEach((w) => {
        const t = { hours: 0, people: 0 };
        w.days.forEach((d) => {
            const dt = { hours: 0, people: 0 };
            (rows || []).forEach((r) => {
                const c = mode === 'hj' ? r.hj[d] : r.cells[d];
                if (!c || (mode !== 'hj' && (c.status === 'missing' || c.status === 'hj'))) return;
                dt.hours += Number(c.hours) || 0;
                dt.people += Number(c.people) || 0;
            });
            days[d] = dt;
            t.hours += dt.hours;
            t.people += dt.people;
        });
        wk[w.weekEnd] = t;
    });
    return { days, weeks: wk };
};

/**
 * Hours per job, code and day over the days given: { job: { code: { date:
 * hours } } }. HeavyJob's hours for a foreman-day it has (hjLabor), the
 * report's hours otherwise — the same rule as codeRollup. Absent crew and
 * rows with no code don't count.
 */
export const codeHoursByDay = (reports, hjLabor, hjCards, days) => {
    const inHj = new Set((hjCards || []).map((c) => `${c.foremanEmp}|${c.job}|${dayOf(c.date)}`));
    const out = {};
    const add = (job, code, d, h) => {
        if (!h || !code || !days.includes(d)) return;
        const j = out[job] || (out[job] = {});
        const c = j[code] || (j[code] = {});
        c[d] = (c[d] || 0) + h;
    };
    (reports || []).forEach((rep) => {
        const d = dayOf(rep.date);
        (rep.labor || []).forEach((x) => {
            if (x.absent) return;
            const job = String(x.job || rep.job);
            if (rep.foremanEmp != null && inHj.has(`${rep.foremanEmp}|${job}|${d}`)) return;
            add(job, x.code, d, (Number(x.st) || 0) + (Number(x.ot) || 0));
        });
    });
    (hjLabor || []).forEach((x) => add(String(x.job), x.code, dayOf(x.date), Number(x.hours) || 0));
    return out;
};

// Series colors: the Strike chart order (docs/STRIKE_STYLE.md section 7).
export const CHART_COLORS = ['#1d518c', '#3f8fd9', '#d2691e', '#8d5fb3', '#0b8a6f'];
export const CHART_OTHER = '#a3a3a3';

/**
 * One job's chart series: the n busiest codes over the window, most hours
 * first, then everything else folded into Other. Each: { code, activity,
 * color, byDay: { date: hours }, hours, budgetHrs, hrsToDate, pct } where
 * pct is hours to date over the budget (null without a budget).
 */
export const chartSeries = (byCode, reg, job, n = 5) => {
    const codes = Object.entries(byCode || {}).map(([code, byDay]) => ({
        code, byDay, hours: Object.values(byDay).reduce((a, b) => a + b, 0),
    })).sort((a, b) => b.hours - a.hours || a.code.localeCompare(b.code));
    const top = codes.slice(0, n).map((c, i) => {
        const r = reg[`${job}|${c.code}`] || {};
        const budget = r.budgetHrs > 0 ? r.budgetHrs : null;
        return {
            ...c, activity: r.activity || '', color: CHART_COLORS[i],
            budgetHrs: budget, hrsToDate: r.hrsToDate || 0, pct: budget ? (r.hrsToDate || 0) / budget : null,
        };
    });
    const rest = codes.slice(n);
    if (rest.length) {
        const byDay = {};
        rest.forEach((c) => Object.entries(c.byDay).forEach(([d, h]) => { byDay[d] = (byDay[d] || 0) + h; }));
        top.push({
            code: 'Other', activity: `${rest.length} more code${rest.length === 1 ? '' : 's'}`, color: CHART_OTHER,
            byDay, hours: rest.reduce((a, c) => a + c.hours, 0), budgetHrs: null, hrsToDate: 0, pct: null,
        });
    }
    return top;
};

/**
 * Registry lookup: `${job}|${code}` -> { activity, uom, tracked, budgetQty,
 * budgetHrs, qtyToDate, hrsToDate, inList } (budgets null when the code has
 * none; inList false for a code charged in HeavyJob that isn't in the code list).
 */
export const registryIndex = (registry) => {
    const out = {};
    const n = (v) => (v == null || v === '' ? null : Number(v));
    (registry || []).forEach((r) => {
        out[`${r.job}|${r.code}`] = {
            activity: r.activity || '', uom: r.uom || '', tracked: !!r.tracked,
            budgetQty: n(r.budgetQty), budgetHrs: n(r.budgetHrs),
            qtyToDate: Number(r.qtyToDate) || 0, hrsToDate: Number(r.hrsToDate) || 0,
            inList: r.inList !== false,
        };
    });
    return out;
};

/**
 * Per job × code over the reports given: { key, job, code, activity, uom,
 * tracked, budgets and to-date, hours, qty, qtyUom, foremen[], lastDay },
 * grouped by job, most hours first within a job.
 * Absent crew rows don't count. Codes with only a quantity still show.
 * Hours for a foreman's day on a job that HeavyJob has (a card in hjCards)
 * come from HeavyJob (hjLabor); the report's hours for that day are left out.
 * Quantities always come from the reports.
 */
export const codeRollup = (reports, reg, hjLabor = [], hjCards = []) => {
    const inHj = new Set((hjCards || []).map((c) => `${c.foremanEmp}|${c.job}|${dayOf(c.date)}`));
    const m = new Map();
    const get = (job, code) => {
        const key = `${job}|${code}`;
        if (!m.has(key)) {
            const r = reg[key] || {};
            m.set(key, {
                key, job, code, activity: r.activity || '', uom: r.uom || '', tracked: !!r.tracked,
                budgetQty: r.budgetQty ?? null, budgetHrs: r.budgetHrs ?? null,
                qtyToDate: r.qtyToDate || 0, hrsToDate: r.hrsToDate || 0,
                known: !!reg[key] && r.inList !== false, hours: 0, qty: 0, qtyUom: '', foremen: new Set(), lastDay: '',
            });
        }
        return m.get(key);
    };
    (reports || []).forEach((rep) => {
        const d = dayOf(rep.date);
        (rep.labor || []).forEach((x) => {
            if (x.absent || !x.code) return;
            const h = (Number(x.st) || 0) + (Number(x.ot) || 0);
            if (!h) return;
            const job = String(x.job || rep.job);
            if (rep.foremanEmp != null && inHj.has(`${rep.foremanEmp}|${job}|${d}`)) return;
            const e = get(job, x.code);
            e.hours += h;
            e.foremen.add(rep.foremanName);
            if (d > e.lastDay) e.lastDay = d;
        });
        (rep.qty || []).forEach((x) => {
            if (!x.code || !Number(x.qty)) return;
            const e = get(String(x.job || rep.job), x.code);
            e.qty += Number(x.qty);
            if (!e.qtyUom) e.qtyUom = x.uom || '';
            if (d > e.lastDay) e.lastDay = d;
        });
    });
    (hjLabor || []).forEach((x) => {
        const h = Number(x.hours) || 0;
        if (!h || !x.code) return;
        const d = dayOf(x.date);
        const e = get(String(x.job), x.code);
        if (!e.activity) e.activity = x.activity || '';
        e.hours += h;
        e.foremen.add(x.foremanName);
        if (d > e.lastDay) e.lastDay = d;
    });
    return [...m.values()]
        .map((e) => ({ ...e, foremen: [...e.foremen].filter(Boolean).sort() }))
        .sort((a, b) => a.job.localeCompare(b.job) || b.hours - a.hours || a.code.localeCompare(b.code));
};

/** Codes with hours and nothing installed. Budgeted (quantity) codes first. */
export const noQtyCodes = (codes) => codes
    .filter((c) => c.hours > 0 && !(c.qty > 0))
    .sort((a, b) => Number(b.tracked) - Number(a.tracked) || b.hours - a.hours);

/**
 * The n codes the table has room for, taken in turn from each job (its most
 * hours first) so one busy job can't push the others off the board; kept in
 * codeRollup's order (by job, most hours first).
 */
export const topCodes = (codes, n) => {
    if (codes.length <= n) return codes;
    const byJob = new Map();
    codes.forEach((c) => { if (!byJob.has(c.job)) byJob.set(c.job, []); byJob.get(c.job).push(c); });
    const pick = new Set();
    for (let i = 0; pick.size < n; i += 1) {
        [...byJob.values()].forEach((list) => { if (list[i] && pick.size < n) pick.add(list[i]); });
    }
    return codes.filter((c) => pick.has(c));
};

// Short job names, as in VoltaGrid Field (lib/config.ts) and on the Volta-grid SharePoint site.
export const JOB_NAMES = { 54026071: 'Block 4', 54026070: 'QPAC Blitz', 54026076: 'Block 5', 54026077: 'General Services' };
const jobName = (j) => JOB_NAMES[j] || '';

// Codes the 10/2 re-code retired, old -> new (Block 4's underground work moved
// to the Underground Electrical / Underground Pipe codes; see commit f282c0f).
// HeavyJob and the code list still carry the old ones, so hours can land there.
export const REPLACED_CODES = {
    54026071: { 120610: '220610', 130607: '230607', 130608: '230608', 141201: '260201', 160326: '260326', 160327: '260327' },
};

/**
 * The cost code table's rows with each job's old codes (REPLACED_CODES) moved
 * below its current ones, each carrying replacedBy (the new code, else '').
 * Order is otherwise kept (by job, most hours first).
 */
export const oldCodesLast = (codes) => codes
    .map((c) => ({ ...c, replacedBy: (REPLACED_CODES[c.job] || {})[c.code] || '' }))
    .sort((a, b) => a.job.localeCompare(b.job) || Number(!!a.replacedBy) - Number(!!b.replacedBy));

const fmtH = (h) => (Math.round(h * 10) / 10).toLocaleString();

// ── Look: Strike AI template (docs/STRIKE_STYLE.md) ──

const T = {
    bg: '#fafafa', card: '#ffffff', ink: '#0a0a0a', ink2: '#525252', muted: '#737373',
    border: '#e5e5e5', fill: '#f5f5f5', navy: '#1d518c', navyTint: '#e8eef5', red: '#d13138',
    good: '#15803d', goodText: '#166534', goodTint: '#e9f6ee',
    warn: '#a16207', warnText: '#854d0e', warnTint: '#fdf4dc',
    bad: '#d13138', badText: '#b02329', badTint: '#fbeaea',
};

const CELL = {
    approved: { label: 'Approved', glyph: '✓', fill: T.goodTint, stroke: T.good, ink: T.goodText, solid: true },
    submitted: { label: 'Submitted', glyph: '✓', fill: T.goodTint, stroke: T.good, ink: T.goodText },
    draft: { label: 'Not submitted', glyph: '…', fill: T.warnTint, stroke: T.warn, ink: T.warnText },
    returned: { label: 'Sent back', glyph: '✕', fill: T.badTint, stroke: T.bad, ink: T.badText },
    missing: { label: 'No report', glyph: '', fill: '#ffffff', stroke: '#c4c4c4', ink: T.muted, dashed: true },
    hj: { label: 'HeavyJob', glyph: '', fill: T.navyTint, stroke: T.navy, ink: T.navy },
};

const CSS = `
.frb{position:fixed;inset:0;z-index:2000;background:${T.bg};color:${T.ink};font-family:"Geist",ui-sans-serif,system-ui,"Segoe UI",sans-serif;
  display:flex;flex-direction:column;overflow:hidden;-webkit-font-smoothing:antialiased}
.frb.idle{cursor:none}
.frb *{box-sizing:border-box}
.frb-brand{display:flex;align-items:center;gap:0.9vw;margin-right:0.6vw}
.frb-brand img{height:2.8vh;width:auto;display:block}
.frb-brand img.blend{mix-blend-mode:multiply}
.frb-sep{width:1px;height:3vh;background:${T.border}}
.frb-clock{margin-left:auto;text-align:right;white-space:nowrap;flex-shrink:0}
.frb-clock b{display:block;font-size:clamp(16px,1.3vw,28px);font-weight:600;font-variant-numeric:tabular-nums;line-height:1}
.frb-clock span{display:block;font-size:clamp(11px,0.7vw,14px);color:${T.ink2};margin-top:2px}
.frb-grid .h.wkh{display:flex;justify-content:space-between;align-items:baseline;gap:0.5vw;flex-wrap:wrap}
.frb-grid .h.wkh .tot{font-weight:600;color:${T.ink2};font-variant-numeric:tabular-nums;white-space:nowrap}
.frb-grid .n.tot{font-weight:600;background:${T.bg};border-top:2px solid ${T.border};margin-top:0.4vh;padding:0.5vh 0 0.5vh 0.4vw;border-radius:8px 0 0 8px}
.frb-c.tot{border:0;border-top:2px solid ${T.border};border-radius:0;margin:0.4vh 0 0;padding:0.5vh 0;height:auto;max-height:none;color:${T.ink};background:${T.bg};line-height:1;align-self:stretch}
.frb-c.tot b{font-size:clamp(12px,0.82vw,18px)}
.frb-c.tot small{font-size:clamp(11px,0.6vw,12px);color:${T.ink2}}
.frb-c.tot.sun{background:${T.bg}}
.frb-grid .wk.tot{background:${T.bg};border-top:2px solid ${T.border};margin-top:0.4vh}
.frb-chart{flex:1;min-height:0;display:flex;gap:1vw;overflow:hidden}
.frb-plot{flex:1;min-width:0;min-height:0;position:relative}
.frb-keys{width:36%;min-width:0;display:flex;flex-direction:column;justify-content:flex-start;gap:0.2vh;font-size:clamp(11px,0.68vw,14px);overflow:hidden;line-height:1.2}
.frb-key{display:flex;gap:0.5vw;align-items:flex-start;min-width:0}
.frb-key i{flex-shrink:0;width:9px;height:9px;border-radius:2px;margin-top:3px}
.frb-key .t{min-width:0}
.frb-key .t div{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.frb-key .t .s{color:${T.ink2};font-size:clamp(11px,0.66vw,14px)}
.frb-c.tot.sun{background:transparent}
.frb-body{flex:1;display:grid;grid-template-columns:repeat(12,minmax(0,1fr));grid-template-rows:minmax(0,1fr);gap:0.9vw;padding:1.4vh 1.6vw 0.4vh;min-height:0}
.frb-card{background:${T.card};border:1px solid ${T.border};border-radius:14px;box-shadow:0 1px 2px 0 rgb(0 0 0 / 0.05);padding:1.4vh 1.1vw;min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden}
.frb-lbl{font-size:clamp(11px,0.72vw,16px);font-weight:500;letter-spacing:0.04em;color:${T.muted};text-transform:uppercase;white-space:nowrap}
.frb-sub{font-size:clamp(11px,0.82vw,18px);color:${T.ink2};margin-top:0.5vh;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.frb-head{display:flex;align-items:baseline;gap:1vw;margin-bottom:0.9vh}
.frb-head h3{margin:0;font-size:clamp(14px,1.05vw,23px);font-weight:600;white-space:nowrap}
.frb-head .frb-lbl{margin-left:auto}
.frb-legend{display:flex;flex-wrap:wrap;gap:0.4vh 0.9vw;font-size:clamp(11px,0.76vw,17px);color:${T.ink2};white-space:nowrap;align-items:center;min-width:0}
.frb-legend i{display:inline-block;width:0.7vw;height:0.7vw;min-width:10px;min-height:10px;border-radius:3px;margin-right:0.35vw;vertical-align:-1px}
.frb-grid{flex:1;min-height:0;display:grid;align-content:start;overflow:hidden}
.frb-grid.fill{align-content:stretch}
.frb-grid .h{font-size:clamp(11px,0.7vw,15px);color:${T.muted};text-align:center;font-weight:500;line-height:1.2;padding-bottom:0.6vh}
.frb-grid .h.sun{color:#a3a3a3}
.frb-grid .h.today{color:${T.ink};font-weight:600}
.frb-grid .n{display:flex;align-items:center;gap:0.5vw;min-width:0;padding-right:0.6vw;font-size:clamp(12px,0.8vw,18px);font-weight:500}
.frb-grid .n span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.frb-tbl tr.job td{background:${T.bg};font-weight:600;padding-top:0.9vh}
.frb-tbl tr.job td span{font-weight:400;color:${T.ink2};margin-left:0.5vw}
.frb-tbl tr.old td{font-size:clamp(11px,0.68vw,15px);font-weight:500;letter-spacing:0.04em;text-transform:uppercase;color:${T.muted};padding-top:0.9vh}
.frb-tbl tr.old td span{text-transform:none;letter-spacing:0;font-weight:400;margin-left:0.5vw}
.frb-tag{flex-shrink:0;font-size:clamp(11px,0.66vw,14px);font-weight:500;padding:1px 6px;border-radius:9999px;background:${T.fill};color:${T.ink2}}
.frb-body.two{grid-template-rows:minmax(0,2.6fr) minmax(0,1fr)}
.frb-grid .wk{display:flex;align-items:center;justify-content:center}
.frb-grid .wk i{display:block;width:1px;height:70%;background:${T.border}}
.frb-grid .h.wkh{color:${T.ink};font-weight:600;text-transform:none;letter-spacing:0;padding-bottom:0.3vh;border-bottom:1px solid ${T.border};margin:0 0.18vw 0.4vh;text-align:left;align-self:end;font-size:clamp(11px,0.72vw,15px)}
.frb-c{height:auto;align-self:stretch;max-height:44px;margin:0.2vh 0.15vw;border-radius:7px;display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1.05;font-variant-numeric:tabular-nums;white-space:nowrap;overflow:hidden}
.frb-c b i,.frb-c small i{font-style:normal;font-weight:400;opacity:.75;margin-left:1px;margin-right:3px}
.frb-c:not(.tot){container-type:size}
/* A short row (a small screen or a long crew list): the hours alone, smaller, so nothing is cut off. */
@container (max-height: 30px){.frb-c small{display:none}.frb-c b{font-size:11px}}
@container (max-height: 38px){.frb-c b{font-size:clamp(11px,0.62vw,13px)}.frb-c small{font-size:11px}}
.frb-c small .job{font-weight:600;margin-left:4px}
.frb-tbl{width:100%;border-collapse:collapse;font-size:clamp(12px,0.82vw,18px)}
.frb-tbl th{font-size:clamp(11px,0.68vw,15px);font-weight:500;color:${T.muted};text-align:left;background:${T.bg};border-bottom:1px solid ${T.border};padding:0.6vh 0.5vw;white-space:nowrap}
.frb-tbl td{padding:0.62vh 0.5vw;border-bottom:1px solid ${T.border};white-space:nowrap}
.frb-tbl td.num,.frb-tbl th.num{text-align:right;font-variant-numeric:tabular-nums}
.frb-tbl td.act{max-width:0;width:100%;overflow:hidden;text-overflow:ellipsis;color:${T.ink2}}
.frb-badge{display:inline-flex;align-items:center;gap:4px;padding:1px 8px;border-radius:9999px;font-size:clamp(11px,0.66vw,14px);font-weight:500}
.frb-item{display:flex;gap:0.8vw;align-items:baseline;padding:0.8vh 0;border-top:1px solid ${T.border}}
.frb-item:first-child{border-top:none;padding-top:0.2vh}
.frb-item b{font-size:clamp(12px,0.9vw,20px);font-weight:600;font-variant-numeric:tabular-nums}
.frb-item .t{flex:1;min-width:0}
.frb-item .t div{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.frb-item .t .s{font-size:clamp(11px,0.76vw,16px);color:${T.ink2};margin-top:0.2vh}
.frb-item .h{font-size:clamp(12px,0.9vw,20px);font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap}
.frb-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;color:${T.muted};font-size:clamp(12px,0.95vw,20px);text-align:center;gap:0.8vh;padding:0 1vw}
.frb-foot{display:flex;align-items:center;gap:1vw;padding:0.6vh 1.6vw 1vh;font-size:clamp(11px,0.7vw,15px);color:${T.muted};flex-shrink:0}
.frb-dot{width:7px;height:7px;border-radius:50%;background:${T.good}}
.frb-dot.err{background:${T.warn}}
.frb-ctl{position:absolute;right:1.6vw;bottom:3.4vh;display:flex;gap:8px;transition:opacity .15s}
.frb.idle .frb-ctl{opacity:0;pointer-events:none}
.frb-ctl button{background:#fff;color:${T.ink};border:1px solid ${T.border};border-radius:10px;height:32px;padding:0 12px;font:500 14px "Geist",system-ui;cursor:pointer;box-shadow:0 1px 2px 0 rgb(0 0 0 / 0.05);transition:background-color .15s}
.frb-ctl button:hover{background:${T.fill}}
.frb-ctl button:focus-visible{outline:none;border-color:${T.navy};box-shadow:0 0 0 3px color-mix(in srgb,${T.navy} 30%,transparent)}
.frb-skel{background:${T.fill};border-radius:8px}
@media (prefers-reduced-motion: reduce){.frb *{transition:none!important}}
`;

// ── Pieces ──

const fmtWeek = (weekEnd) => `Wk ending ${moment.utc(weekEnd, 'YYYY-MM-DD').format('M/D')}`;

// Columns: name, then each week's seven days with a spacer column between
// the weeks (the ticket strip puts the week summary there).
const gridColumns = (weeks, nameFr = 1.7) => `minmax(0,${nameFr}fr) ${weeks.map((w) => `repeat(${w.days.length}, minmax(0,1fr))`).join(' minmax(0,0.9fr) ')}`;

// Header rows shared by the three grids: a week label over each week, then
// the day names. weekCell is what the spacer column's header says.
const GridHead = ({ weeks, today, weekCell, totals }) => (
    <>
        <div />
        {weeks.map((w, i) => (
            <React.Fragment key={w.weekEnd}>
                {i ? <div /> : null}
                <div className="h wkh" style={{ gridColumn: `span ${w.days.length}` }}>
                    {fmtWeek(w.weekEnd)}
                    {totals ? <span className="tot">{fmtH(totals.weeks[w.weekEnd]?.hours || 0)} h · {totals.weeks[w.weekEnd]?.people || 0} ppl</span> : null}
                </div>
            </React.Fragment>
        ))}
        {weekCell ? <div /> : null}
        <div />
        {weeks.map((w, i) => (
            <React.Fragment key={w.weekEnd}>
                {i ? <div className="h" style={{ alignSelf: 'end' }}>{weekCell || ''}</div> : null}
                {w.days.map((d) => {
                    const m = moment.utc(d, 'YYYY-MM-DD');
                    return (
                        <div key={d} className={`h${isSunday(d) ? ' sun' : ''}${d === today ? ' today' : ''}`} style={{ alignSelf: 'end' }}>
                            {m.format('dd')}<br />{m.format('M/D')}
                        </div>
                    );
                })}
            </React.Fragment>
        ))}
        {weekCell ? <div className="h" style={{ alignSelf: 'end' }}>{weekCell}</div> : null}
    </>
);

const Spacer = () => <div className="wk"><i /></div>;

// One foreman-day as filed in VoltaGrid Field. A day HeavyJob has and no
// report is "no report" once a report was due, else blank.
const ReportCell = ({ r, d, c }) => {
    const sun = isSunday(d);
    if (!c || (c.status === 'hj' && !c.missing)) return <div className={`frb-c${sun ? ' sun' : ''}`} />;
    const status = c.status === 'hj' ? 'missing' : c.status;
    const s = CELL[status];
    const title = status === 'missing'
        ? `${r.name} · ${d} — no report`
        : `${r.name} · ${d} — ${Object.entries(c.byJob).map(([j, st]) => `${j} ${CELL[st].label.toLowerCase()}`).join(', ')} · ${fmtH(c.hours)} h · ${c.people} people`;
    return (
        <div
            className="frb-c"
            title={title}
            style={{
                background: s.solid ? s.stroke : s.fill,
                color: s.solid ? '#fff' : s.ink,
                border: `1.5px ${s.dashed ? 'dashed' : 'solid'} ${s.stroke}`,
            }}
        >
            {status === 'missing' ? null : (
                <>
                    <b>{fmtH(c.hours)}</b>
                    {/* People, then the jobs filed that day; a job still in progress takes its own status colour. */}
                    <small>
                        {c.people}
                        {Object.keys(c.byJob).sort().map((j) => (
                            <span key={j} className="job" style={c.byJob[j] !== c.status ? { color: CELL[c.byJob[j]].ink } : undefined}>{String(j).slice(-2)}</span>
                        ))}
                    </small>
                </>
            )}
        </div>
    );
};

// The same foreman-day as keyed into HeavyJob: the card's hours and people,
// the jobs, and whether it matches the report (a job that differs reads red).
// A report day with no card is "no card".
const HjCell = ({ r, d, c, card }) => {
    const sun = isSunday(d);
    const report = c && c.status !== 'missing' && c.status !== 'hj';
    if (!card) {
        if (!report) return <div className={`frb-c${sun ? ' sun' : ''}`} />;
        return (
            <div className="frb-c" title={`${r.name} · ${d} — report filed (${fmtH(c.hours)} h), no HeavyJob card`}
                style={{ background: '#fff', color: T.muted, border: '1.5px dashed #c4c4c4' }}>
                <small>no card</small>
            </div>
        );
    }
    const check = report ? hjCheck(c.jobHours, card) : null;
    const bad = check && !check.ok ? new Set(check.diffs.map((x) => x.job)) : null;
    const s = CELL.hj;
    const title = `${r.name} · ${d} — HeavyJob: ${Object.entries(card.byJob).map(([j, x]) => `${j} ${fmtH(x.hours)} h`).join(', ')} · ${card.people} people${card.approved ? ' · approved' : ' · not approved'}`
        + (check ? (check.ok ? ' · same hours as the report' : ` · differs: ${check.diffs.map((x) => `${x.job} report ${fmtH(x.report)} h, HeavyJob ${fmtH(x.hj)} h`).join('; ')}`) : ' · no report');
    return (
        <div className="frb-c" title={title}
            style={{ background: s.fill, color: s.ink, border: `1.5px solid ${bad ? T.bad : s.stroke}` }}>
            <b>
                {fmtH(card.hours)}
                {bad ? <span className="hjk bad">≠</span> : check ? <span className="hjk ok">=</span> : null}
            </b>
            <small>
                {card.people}
                {Object.keys(card.byJob).sort().map((j) => (
                    <span key={j} className="job" style={bad && bad.has(j) ? { color: T.badText, textDecoration: 'underline' } : undefined}>{String(j).slice(-2)}</span>
                ))}
            </small>
        </div>
    );
};

// Foremen down, days across, two weeks. mode 'report' draws VoltaGrid
// Field's reports, mode 'hj' the HeavyJob cards — same rows, same columns,
// so the two sit side by side.
const ForemanGrid = ({ rows, weeks, today, mode, totals }) => (
    <div className="frb-grid fill" style={{ gridTemplateColumns: gridColumns(weeks), gridTemplateRows: `auto auto repeat(${rows.length}, minmax(0,1fr)) auto` }}>
        <GridHead weeks={weeks} today={today} totals={totals} />
        {rows.map((r) => (
            <React.Fragment key={r.key}>
                <div className="n">
                    <span>{r.name}</span>
                    {r.hjOnly && mode === 'report' ? <span className="frb-tag" title="In HeavyJob, no report yet">HJ</span> : null}
                </div>
                {weeks.map((w, i) => (
                    <React.Fragment key={w.weekEnd}>
                        {i ? <Spacer /> : null}
                        {w.days.map((d) => (mode === 'hj'
                            ? <HjCell key={d} r={r} d={d} c={r.cells[d]} card={r.hj[d]} />
                            : <ReportCell key={d} r={r} d={d} c={r.cells[d]} />))}
                    </React.Fragment>
                ))}
            </React.Fragment>
        ))}
        {/* Day totals across the foremen. */}
        <div className="n tot"><span>Total</span></div>
        {weeks.map((w, i) => (
            <React.Fragment key={w.weekEnd}>
                {i ? <div className="wk tot" /> : null}
                {w.days.map((d) => {
                    const t = totals.days[d] || { hours: 0, people: 0 };
                    return (
                        <div key={d} className={`frb-c tot${isSunday(d) ? ' sun' : ''}`} title={`${d} — ${fmtH(t.hours)} h · ${t.people} people`}>
                            {t.hours ? <><b>{fmtH(t.hours)}</b><small>{t.people}<i>ppl</i></small></> : null}
                        </div>
                    );
                })}
            </React.Fragment>
        ))}
    </div>
);

const fmtPct = (p) => `${Math.round(p * 100)}%`;

// One job's hours by cost code, day by day: stacked bars (busiest codes in
// the Strike series colors, the rest as Other) with the legend beside them
// naming each code and its hours to date against the budget.
const CodeChart = ({ job, series, days, today }) => {
    const data = {
        labels: days.map((d) => moment.utc(d, 'YYYY-MM-DD').format('dd M/D')),
        datasets: series.map((x) => ({
            label: x.code, data: days.map((d) => Math.round((x.byDay[d] || 0) * 10) / 10),
            backgroundColor: x.color, borderColor: '#fff', borderWidth: { top: 0, right: 0, bottom: 2, left: 0 }, borderSkipped: false,
            borderRadius: 3, maxBarThickness: 48,
        })),
    };
    const options = {
        animation: false, responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${fmtH(c.raw)} h` } },
        },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: T.ink2, font: { family: 'Geist', size: 11 } } },
            y: { stacked: true, grid: { color: T.border }, border: { display: false }, ticks: { color: T.muted, font: { family: 'Geist', size: 11 }, maxTicksLimit: 5 } },
        },
    };
    const total = series.reduce((a, x) => a + x.hours, 0);
    return (
        <div className="frb-card" style={{ flex: 1 }}>
            <div className="frb-head" style={{ marginBottom: '0.4vh', flexShrink: 0 }}>
                <h3>{jobName(job) || job} <span style={{ fontWeight: 400, color: T.ink2 }}>{job}</span></h3>
                <span className="frb-lbl">{fmtH(total)} h by cost code · {days.length} days</span>
            </div>
            {series.length ? (
                <div className="frb-chart">
                    <div className="frb-plot"><Bar data={data} options={options} /></div>
                    <div className="frb-keys">
                        {series.map((x) => (
                            <div key={x.code} className="frb-key" title={x.activity ? `${x.code} ${x.activity}` : x.code}>
                                <i style={{ background: x.color }} />
                                <div className="t">
                                    <div><b>{x.code}</b> {x.activity}</div>
                                    <div className="s">
                                        {fmtH(x.hours)} h
                                        {x.pct != null ? <> · <b style={x.pct > 1 ? { color: T.badText } : undefined}>{fmtPct(x.pct)}</b> of {fmtH(Math.round(x.budgetHrs))} budget h</> : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : <div className="frb-empty">No hours charged on {jobName(job) || job} in the last {days.length} days.</div>}
        </div>
    );
};

// ── Board ──

const FieldReportsBoard = ({ onExit }) => {
    const [headers, setHeaders] = useState([]);
    const [reports, setReports] = useState([]);
    const [registry, setRegistry] = useState([]);
    const [hjCards, setHjCards] = useState([]);
    const [hjLabor, setHjLabor] = useState([]);
    // False when the server read predates the HeavyJob recordsets (no hj key).
    const [hjKnown, setHjKnown] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [now, setNow] = useState(moment());
    const [idle, setIdle] = useState(false);
    const rootRef = useRef(null);
    const startedAt = useRef(Date.now());

    // A failed refresh keeps the last good data and the footer dot says so.
    useEffect(() => {
        let dead = false;
        const ok = (res) => res && !res.error;
        const pass = async () => {
            const span = boardDays(ymd(moment()));
            const res = await loadVgFieldBoard({ from: span[0], to: ymd(moment()), codeFrom: span[0] }).catch(() => null);
            if (dead) return;
            let bad = false;
            if (ok(res) && Array.isArray(res.days)) {
                setHeaders(res.days);
                setReports(res.reports || []);
                setRegistry(res.registry || []);
                setHjKnown(Array.isArray(res.hj));
                setHjCards((res.hj || []).filter((c) => Number(c.hours) > 0));
                setHjLabor(res.hjLabor || []);
                setUpdatedAt(moment());
            } else {
                bad = true;
            }
            setFailed(bad);
            setLoaded(true);
        };
        pass();
        const id = setInterval(pass, REFRESH_MS);
        return () => { dead = true; clearInterval(id); };
    }, []);

    // Clock, plus a 4 AM reload so the screen picks up deploys.
    useEffect(() => {
        const id = setInterval(() => {
            const m = moment();
            setNow(m);
            if (m.hour() === 4 && Date.now() - startedAt.current > 6 * 3600 * 1000) window.location.reload();
        }, 15000);
        return () => clearInterval(id);
    }, []);

    // Keep the monitor awake where the browser allows.
    useEffect(() => {
        let lock = null;
        const grab = async () => {
            try {
                if (navigator.wakeLock && document.visibilityState === 'visible') lock = await navigator.wakeLock.request('screen');
            } catch (e) { /* unsupported or denied */ }
        };
        grab();
        const onVis = () => { if (document.visibilityState === 'visible') grab(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            if (lock) lock.release().catch(() => {});
        };
    }, []);

    // Cursor and controls fade after a few seconds without the mouse.
    useEffect(() => {
        let t = null;
        const wake = () => {
            setIdle(false);
            clearTimeout(t);
            t = setTimeout(() => setIdle(true), 3500);
        };
        wake();
        window.addEventListener('mousemove', wake);
        window.addEventListener('keydown', wake);
        return () => { clearTimeout(t); window.removeEventListener('mousemove', wake); window.removeEventListener('keydown', wake); };
    }, []);

    const today = ymd(now);
    // The last eight days, split where the billing week ends.
    const days = useMemo(() => boardDays(today), [today]);
    const weeks = useMemo(() => weeksOf(days), [days]);
    const reportRows = useMemo(() => foremanRows(headers, days, today, hjCards), [headers, days, today, hjCards]);
    // The same foremen on both grids, HeavyJob-only ones included.
    const rows = useMemo(() => boardRows(reportRows, hjCards, days), [reportRows, hjCards, days]);
    const reportTotals = useMemo(() => totalsOf(rows, weeks, 'report'), [rows, weeks]);
    const hjTotals = useMemo(() => totalsOf(rows, weeks, 'hj'), [rows, weeks]);
    const reg = useMemo(() => registryIndex(registry), [registry]);
    // One chart per job with hours in the window, most hours first.
    const charts = useMemo(() => {
        const byJob = codeHoursByDay(reports, hjLabor, hjCards, days);
        return Object.keys(byJob)
            .map((job) => ({ job, series: chartSeries(byJob[job], reg, job) }))
            .sort((a, b) => b.series.reduce((x, y) => x + y.hours, 0) - a.series.reduce((x, y) => x + y.hours, 0));
    }, [reports, hjLabor, hjCards, days, reg]);

    const missingN = rows.reduce((s, r) => s + Object.values(r.cells).filter((c) => c.status === 'missing' || c.missing).length, 0);
    const hjOffN = rows.reduce((s, r) => s + Object.values(r.cells).filter((c) => c.hj && !c.hj.ok).length, 0);
    const noCardN = rows.reduce((s, r) => s + days.filter((d) => {
        const c = r.cells[d];
        return c && c.status !== 'missing' && c.status !== 'hj' && !r.hj[d];
    }).length, 0);

    const toggleFs = () => {
        const el = rootRef.current;
        if (!document.fullscreenElement && el?.requestFullscreen) el.requestFullscreen().catch(() => {});
        else if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };

    return (
        <div className={`frb${idle ? ' idle' : ''}`} ref={rootRef}>
            <style>{CSS}</style>

            {loaded && !updatedAt ? (
                <div className="frb-body" style={{ gridTemplateRows: 'minmax(0,1fr)' }}>
                    <div className="frb-card" style={{ gridColumn: '1/-1' }}>
                        <div className="frb-empty">
                            <b style={{ color: T.ink, fontSize: 'clamp(16px,1.3vw,28px)' }}>Can’t reach the field reports right now</b>
                            <span>The board tries again every 5 minutes.</span>
                        </div>
                    </div>
                </div>
            ) : (
            <div className="frb-body two">
                <div className="frb-card" style={{ gridColumn: 'span 6' }}>
                    <div className="frb-head" style={{ marginBottom: '0.5vh', alignItems: 'center' }}>
                        <div className="frb-brand">
                            <img src={strikeLogo} alt="Strike" />
                            <div className="frb-sep" />
                            <img src={voltaLogo} alt="VoltaGrid" className="blend" />
                        </div>
                        <h3>Field Ticket App</h3>
                        {missingN ? <span className="frb-lbl" style={{ color: T.badText }}>{missingN} missing</span> : null}
                    </div>
                    <div className="frb-legend" style={{ marginBottom: '0.9vh' }}>
                        {['submitted', 'approved', 'draft', 'returned', 'missing'].map((k) => (
                            <span key={k}>
                                <i style={{ background: CELL[k].solid ? CELL[k].stroke : CELL[k].fill, border: `1.5px ${CELL[k].dashed ? 'dashed' : 'solid'} ${CELL[k].stroke}` }} />
                                {CELL[k].label}
                            </span>
                        ))}
                        <span style={{ color: T.muted }}>hours / people · jobs</span>
                    </div>
                    {!loaded ? <div className="frb-skel" style={{ flex: 1 }} />
                        : rows.length ? <ForemanGrid rows={rows} weeks={weeks} today={today} mode="report" totals={reportTotals} />
                            : <div className="frb-empty">Reports show up here as foremen submit them in VoltaGrid Field.</div>}
                </div>

                <div className="frb-card" style={{ gridColumn: 'span 6' }}>
                    <div className="frb-head" style={{ marginBottom: '0.5vh', alignItems: 'center' }}>
                        <h3>HeavyJob</h3>
                        {hjKnown && (hjOffN || noCardN) ? (
                            <span className="frb-lbl" style={{ color: T.badText, marginLeft: 0 }}>
                                {[hjOffN ? `${hjOffN} differ` : '', noCardN ? `${noCardN} no card` : ''].filter(Boolean).join(' · ')}
                            </span>
                        ) : null}
                        <div className="frb-clock">
                            <b>{now.format('h:mm A')}</b>
                            <span>{now.format('dddd, MMMM D')}</span>
                        </div>
                    </div>
                    <div className="frb-legend" style={{ marginBottom: '0.9vh' }}>
                        <span><i style={{ background: CELL.hj.fill, border: `1.5px solid ${CELL.hj.stroke}` }} />Card</span>
                        <span><i style={{ background: CELL.hj.fill, border: `1.5px solid ${T.bad}` }} />Differs</span>
                        <span><i style={{ background: '#fff', border: '1.5px dashed #c4c4c4' }} />No card</span>
                        <span><b style={{ color: T.goodText }}>=</b> matches the app</span>
                        <span style={{ color: T.muted }}>hours / people · jobs</span>
                    </div>
                    {!loaded ? <div className="frb-skel" style={{ flex: 1 }} />
                        : !hjKnown ? <div className="frb-empty"><b style={{ color: T.ink }}>HeavyJob cards aren’t in this feed yet</b><span>The server read needs the HeavyJob update (readVgFieldBoard).</span></div>
                            : rows.length ? <ForemanGrid rows={rows} weeks={weeks} today={today} mode="hj" totals={hjTotals} />
                                : <div className="frb-empty">HeavyJob cards show up here as they are keyed.</div>}
                </div>

                {/* One chart per job with hours in the window. */}
                {!loaded ? <div className="frb-card" style={{ gridColumn: 'span 12' }}><div className="frb-skel" style={{ flex: 1 }} /></div>
                    : charts.length ? charts.map((c) => (
                        <div key={c.job} style={{ gridColumn: `span ${Math.max(3, Math.floor(12 / charts.length))}`, display: 'flex', minHeight: 0 }}>
                            <CodeChart job={c.job} series={c.series} days={days} today={today} />
                        </div>
                    )) : <div className="frb-card" style={{ gridColumn: 'span 12' }}><div className="frb-empty">No hours charged in the last {days.length} days.</div></div>}
            </div>
            )}

            <div className="frb-foot">
                <span className={`frb-dot${failed ? ' err' : ''}`} />
                <span>
                    {updatedAt ? `Updated ${updatedAt.format('h:mm A')}` : loaded ? 'Not loaded yet' : 'Loading…'}
                    {failed ? ' · didn’t refresh, retrying' : ' · refreshes every 5 minutes'}
                </span>
                <span style={{ marginLeft: 'auto' }}>The last {BOARD_DAYS} days, split at the billing week (Monday to Sunday) · code hours from HeavyJob where keyed, else the app · budget hours from the code list</span>
            </div>

            <div className="frb-ctl">
                <button type="button" onClick={toggleFs}>Full screen</button>
                {onExit ? <button type="button" onClick={onExit}>Exit</button> : null}
            </div>
        </div>
    );
};

export default FieldReportsBoard;
